import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { uploadEvidenceToServer } from '../../services/operations/profileAPI';
import { getObservations, submitObservationResponse, getMyAssignedObservations } from '../../services/observationAPI';

const InProgressPage = () => {
    const { observationId } = useParams();
    const navigate = useNavigate();
    const [observation, setObservation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [response, setResponse] = useState('');
    const [attachment, setAttachment] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { user } = useSelector((state) => state.profile);

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (user.accountType === "Admin") {
                    const observationsData = await getObservations();
                    const foundObservation = observationsData.observations.find((obs) => obs._id === observationId);
                    if (foundObservation) {
                        setObservation(foundObservation);
                    }
                }
                else {
                    const observationsData = await getMyAssignedObservations();
                    const foundObservation = observationsData.observations.find((obs) => obs._id === observationId);
                    if (foundObservation) {
                        setObservation(foundObservation);
                    }
                }

            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Failed to load data');
                if (user.accountType === "Admin") {
                    navigate('/admin/my-actions');
                }
                else {
                    navigate('/employee/my-actions');
                }

            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [observationId, navigate, user.accountType, user._id]);

    const handleAttachmentChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setAttachment(e.target.files[0]);
        }
    };

    const handleSubmit = async () => {
        if (!observation) return;

        setIsSubmitting(true);

        if (!response.trim()) {
            toast.error('Please provide a response.');
            setIsSubmitting(false);
            return;
        }

        try {
            let attachmentUrl = '';
            if (attachment) {
                
                const uploadResult = await uploadEvidenceToServer(attachment);
                console.log("re",uploadResult)

                if (uploadResult) {
                    attachmentUrl = uploadResult;
                } else {
                    toast.error('Failed to upload attachment.');
                    setIsSubmitting(false);
                    return; // Stop submission if upload fails
                }
            }

            const responsePayload = {
                responseData: {
                    response,
                    attachment: attachmentUrl,
                },
            };

            await submitObservationResponse(observationId, responsePayload);
            toast.success('Response submitted successfully');
            if (user.accountType === "Admin") {
                navigate('/admin/my-actions');
            }
            else {
                navigate('/employee/my-actions');
            }

        } catch (error) {
            console.error('Error submitting response:', error);
            toast.error('Failed to submit response');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center items-center h-64">Loading...</div>;
    }

    if (!observation) {
        return <div className="text-center">Observation not found.</div>;
    }

    return (
        <div className="max-w-3xl mx-auto p-4 space-y-8">
            <h1 className="text-3xl font-bold">In Progress</h1>

            <div className="bg-white shadow-md rounded-lg p-6 space-y-4">
                <h2 className="text-xl font-semibold">Observation Details</h2>
                <p>Description: {observation?.description}</p>
                <p>Area: {observation.area?.name}</p>
                <p>Reported by: {observation?.observedBy?.firstName} {observation?.observedBy?.lastName} ({observation?.observedBy?.email})</p>
                {observation?.photos[0] && (
                    <p>Image: <img src={observation?.photos[0]} height={400} width={400}/> </p>
                )}
                {observation.correctiveAction?.action && (
                    <p>Corrective Action: {observation.correctiveAction.action}</p>
                )}
                {observation.correctiveAction?.responsiblePerson && (
                    <p>
                        Responsible Person:{' '}
                        {observation.correctiveAction.responsiblePerson.firstName} {observation.correctiveAction.responsiblePerson.lastName} ({observation.correctiveAction.responsiblePerson.email})
                    </p>
                )}
                {observation.correctiveAction?.deadline && (
                    <p>Deadline: {new Date(observation.correctiveAction.deadline).toLocaleDateString()} </p>
                )}
                {observation.responseData?.rejectionReason && (
                    <p className="text-red-500">
                        Rejection Reason: {observation.responseData.rejectionReason}
                    </p>
                )}
            </div>
            {(user._id === observation.correctiveAction?.responsiblePerson?._id)
                ? <>
                    <div className="bg-white shadow-md rounded-lg p-6 space-y-4">
                        <h2 className="text-xl font-semibold">Provide Response</h2>
                        <textarea
                            value={response}
                            onChange={(e) => setResponse(e.target.value)}
                            placeholder="Enter your response..."
                            className="w-full border rounded-md py-2 px-3"
                            required
                        />

                        <div className="mt-4">
                            <label
                                htmlFor="attachment"
                                className="block text-sm font-medium text-gray-700"
                            >
                                Attachment (Optional)
                            </label>
                            <input
                                type="file"
                                id="attachment"
                                onChange={handleAttachmentChange}
                                className="mt-1"
                            />
                            {attachment && (
                                <p className="text-sm text-gray-500">
                                    Selected file: {attachment.name}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                        </button>
                    </div>
                </> :
                null}

            <div className="flex justify-start">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded"
                >
                    Back to List
                </button>
            </div>
        </div>
    );
};

export default InProgressPage;
